import React, { useMemo } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { List } from 'immutable';
import {
    CallRecordItemSkeleton,
    CallRecordSkeleton,
} from 'flow/components/loaders/CallRecordSkeleton';
import Divider from 'flow/components/ui-components/Divider';
import useAppContext from 'flow/hooks/useAppContext';
import { CallRecord } from 'flow/types/CallRecord';
import { TypedMap } from 'flow/types/TypedMap';
import { getDateDescription } from 'flow/utils';
import { getCallRecordsSortedInDateGroups } from 'flow/utils/call-records-utils';
import AICoworkerCallRecordItem from 'flow/pages/ai-coworker/AICoworkerCallRecordItem';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    selectGeneralNextBeforeForExtension,
    selectGeneralSortingDataForExtension,
} from 'flow/selectors/ai-coworker-call-records-selectors';
import { fetchCallRecordsForAICoworker } from 'flow/actions/ai-coworker/call-records-actions';
import {
    useAiCoworkerCallDetailEvals,
    type AICoworkerCallDetailEvals,
} from 'flow/hooks/api/useAiCoworkerCallDetailEvals';

interface Props {
    extensionKey: ExtensionKey;
}

const AiCoworkerCallHistory = ({ extensionKey }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const callRecordsSortingData = useAppSelector((state) =>
        selectGeneralSortingDataForExtension(state, extensionKey),
    );

    const callRecordsSortedInDateGroups = useMemo(
        () => getCallRecordsSortedInDateGroups(callRecordsSortingData),
        [callRecordsSortingData],
    );

    const callRecordsNextBeforeTimestamp = useAppSelector((state) =>
        selectGeneralNextBeforeForExtension(state, extensionKey),
    );

    // Fetch evaluations using infinite query
    const {
        allEvaluations: evaluationsList,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useAiCoworkerCallDetailEvals(
        extensionKey,
        30,
        !!callRecordsSortingData,
    );

    // Create evaluation map from fetched data
    // Map both locutusId and parentLocutusSessionId for queue calls
    const evaluationsMap = useMemo(() => {
        const map = new Map<string, AICoworkerCallDetailEvals>();
        evaluationsList.forEach((evaluation) => {
            // Always map by locutusId
            map.set(evaluation.locutusId, evaluation);
            // Also map by parentLocutusSessionId if it exists (for queue calls)
            if (evaluation.parentLocutusSessionId) {
                map.set(evaluation.parentLocutusSessionId, evaluation);
            }
        });
        return map;
    }, [evaluationsList]);

    const onLoadMore = () => {
        // Fetch more call records
        if (callRecordsNextBeforeTimestamp) {
            void dispatch(
                fetchCallRecordsForAICoworker(
                    extensionKey,
                    undefined,
                    undefined,
                    callRecordsNextBeforeTimestamp,
                ),
            );
        }

        // Also fetch more evaluations if available
        // This ensures we have evaluations for older calls as user scrolls
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    };

    const hasMoreCallRecords = callRecordsNextBeforeTimestamp !== null; // TO DO: improve this logic

    if (callRecordsSortedInDateGroups?.size === 0) {
        return (
            <div className="flex justify-center py-9 text-sm text-gray-500">
                {i18next.t('call.history.title.last')}
            </div>
        );
    }

    /**
     * Take a group of calls made on a date. Then render a date header and the calls in the group.
     */
    const renderDateGroup = (
        callRecords: List<TypedMap<CallRecord>>,
        dateKey: number,
    ) => {
        const header = (
            <Divider key={`datekey-${dateKey}`}>
                {getDateDescription(dateKey, i18next)}
            </Divider>
        );

        const list = callRecords
            .map((callRecord) => {
                // Extract the locutus ID from the call record key (removing 'call-' prefix)
                const locutusId = callRecord
                    .get('key')
                    .substring('call-'.length);

                // For queue calls, the parent session ID should be used to find the evaluation
                // For regular calls, use the normal locutus ID
                // The evaluation map contains entries for both IDs when parentLocutusSessionId exists
                const evaluation = evaluationsMap.get(locutusId);

                return (
                    <AICoworkerCallRecordItem
                        key={`wrapper-${callRecord.get('key')}`}
                        callRecordKey={callRecord.get('key')}
                        displayQueueNameAndDate={false}
                        isCompactView={false}
                        evaluation={evaluation}
                    />
                );
            })
            .valueSeq()
            .toArray();

        return [header, ...list];
    };

    const listItems = [
        ...callRecordsSortedInDateGroups
            .map(renderDateGroup)
            .valueSeq()
            .toArray(),
    ].flat();

    return (
        <InfiniteScroll
            className="!overflow-visible"
            dataLength={listItems.length}
            next={onLoadMore}
            hasMore={hasMoreCallRecords}
            loader={<CallRecordItemSkeleton />}
            scrollableTarget="main-scrollable-div"
            endMessage={
                !hasMoreCallRecords && listItems.length > 0 ? (
                    <div className="flex justify-center py-9 text-sm text-gray-500">
                        {i18next.t('call.history.title.last')}
                    </div>
                ) : null
            }
        >
            {listItems.length > 0 ? listItems : <CallRecordSkeleton />}
        </InfiniteScroll>
    );
};

export default React.memo(AiCoworkerCallHistory);
